import { Row } from 'Components/Grid';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { getLegalBases, getDataCategories } from 'services/mapping-service';

const CATEGORY_COLORS = ['#ed7e30', '#a5a5a5', '#70ad47', '#5b9bd5', '#ffc000', '#9e480e', '#636363', '#997300'];

export const GraphicLegalBaseVolumeData = ({ indicatorVolumeGraphicByLegalBase }) => {
  const { t, i18n } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);

  const { data: legalBasesList } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getLegalBases(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const { data: dataCategoriesList } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getDataCategories(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const categoryByKey = new Map((dataCategoriesList ?? []).map((c) => [c.key, c.name]));

  const categoryKeys = Array.from(
    new Set(
      (indicatorVolumeGraphicByLegalBase ?? []).flatMap((row) =>
        (row.categoryCounts ?? []).map((cc) => cc.categoryKey),
      ),
    ),
  );

  const legalBases = (legalBasesList ?? []).map((lb) => {
    const match = (indicatorVolumeGraphicByLegalBase ?? []).find(
      (item) => item.name === lb.key || item.name === lb.name || item.key === lb.key,
    );
    const row = {
      name: lb.name,
      areaQuantity: match?.areaQuantity ?? 0,
      processQuantity: match?.processQuantity ?? 0,
    };
    categoryKeys.forEach((categoryKey) => {
      const entry = (match?.categoryCounts ?? []).find((cc) => cc.categoryKey === categoryKey);
      row[`category_${categoryKey}`] = entry?.quantity ?? 0;
    });
    return row;
  });

  return (
    <Row className="mt-4">
      <ResponsiveContainer width="100%" height="100%" minHeight={500} minWidth={600}>
        <BarChart
          layout="vertical"
          width={800}
          height={500}
          data={legalBases}
          margin={{
            top: 5,
            right: 10,
            left: 0,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="name" type="category" width={350} fontSize={14} />
          <Tooltip />
          <Legend />
          <Bar
            name={t('ropa.area.processes')}
            stackId={'BarStacked'}
            dataKey="processQuantity"
            barSize={35}
            fill="#4472c5"
          />
          {categoryKeys.map((categoryKey, index) => (
            <Bar
              key={categoryKey}
              name={categoryByKey.get(categoryKey) ?? categoryKey}
              stackId={'BarStacked'}
              dataKey={`category_${categoryKey}`}
              barSize={35}
              fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
            />
          ))}
          <Bar
            name={t('ropa.area.label')}
            stackId={'BarStacked'}
            dataKey="areaQuantity"
            barSize={35}
            fill="#ffc100"
          />
        </BarChart>
      </ResponsiveContainer>
    </Row>
  );
};
